import React, { createContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { APIResponse } from '../../Classes';
import { bindFormChange } from '../../Util';

interface PaginationOptions<T,K>{
	autoLoad?: boolean,
	fetch: (page: number, data?: T)=>Promise<APIResponse<K>>,
	defaultParams?: T,
	fetchOnPageChange?: boolean,
	initialPage?: number,
	errorCode?: string,
	onParamsChange?: (params: T)=>void,
}

interface PaginationHook<T,K>{
	page: number,
	setPage: (p: number)=>void,
	data: K,
	params: T,
	setParams: (p: T)=>void,
	setParamValue: (k: keyof T, p: any)=>void,
	loadError: string,
	loading: boolean,
	fetch: (p?: T)=>void,
	onParamsChange:(name: keyof T, semantic?: boolean, target?: boolean)=>((e: any, data?: any)=>void),
}

var usePagination = <T=any,K=any>(opts: PaginationOptions<T,K>) : PaginationHook<T, K>=>{
	var { t } = useTranslation();
	var [data, setData] = useState<K>(null);
	var [page, setPage] = useState<number>(opts.initialPage || 0);
	var [params, setParams] = useState(opts.defaultParams);
	var [loadError, setLoadError] = useState<string>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var onParamsChange = bindFormChange<T>(params, setParams);
	var paramsRef = useRef<typeof params>(null);
	paramsRef.current = params;

	useEffect(()=>{
		if(opts.onParamsChange) opts.onParamsChange(params);
	}, [params]);

	useEffect(()=>{
		if(opts.autoLoad!==false){
			fetchData(page, params);
		}
	}, []);

	var fetchData = (page: number, params: T)=>{
		setLoading(true);
		setLoadError(null);
		opts.fetch(Math.max(page, 0), params).then(res=>{
			if(res.error) return setLoadError(res.message);
			setData(res.data);
		}).catch(err=>{
			setLoadError(opts.errorCode || t('common.unexpected_code', { code: 'LCL-USPGT-1' }));
		}).finally(()=>{
			setLoading(false);
		})
	}

	var onPageChange = (p: number)=>{
		if(p===page) return;
		setPage(p);
		if(opts.fetchOnPageChange!==false){
			fetchData(p, paramsRef.current)
		}
	}

	return {
		page,
		setPage: onPageChange,
		data,
		params,
		setParams,
		setParamValue: (k, v)=>{
			setParams({
				...((params as any) || {}),
				[k]: v,
			});
		},
		loadError,
		loading,
		fetch: (p?: T)=>{
			if(p) setParams(p);
			fetchData(page, p || params)
		},
		onParamsChange,
	}
}

export default usePagination;